import { api } from "@/lib/api";
import type {
  AlbumItem,
  AlbumType,
  StudioAlbum,
  TaxonomyStatus,
} from "../schema";

const CONTENT_ALBUM = "/content-albums";

export interface AlbumLanguageVariant {
  id: string;
  albumId: string;
  languageCode: string;
  isDefault: boolean;
  localizedTitle: string | null;
  localizedDescription: string | null;
}

export interface AlbumFindAllFilters {
  search?: string;
  status?: TaxonomyStatus;
  categoryId?: string;
  albumType?: AlbumType;
  page?: number;
  limit?: number;
}

export interface AlbumCreatePayload {
  name: string;
  description?: string | null;
  albumType: AlbumType;
  categoryId?: string | null;
  tagOptionIds?: string[];
  coverImage?: string | null;
  primaryLanguageCode?: string;
}

export interface AlbumUpdatePayload {
  name?: string;
  description?: string | null;
  albumType?: AlbumType;
  categoryId?: string | null;
  tagOptionIds?: string[];
  coverImage?: string | null;
}

export interface AlbumVariantPayload {
  languageCode: string;
  isDefault: boolean;
  localizedTitle?: string | null;
  localizedDescription?: string | null;
}

export interface AddAlbumItemPayload {
  contentId: string;
  contentType: "audio" | "video";
}

export const ContentAlbumService = {
  async findAll(filters?: AlbumFindAllFilters): Promise<{
    items: StudioAlbum[];
    pagination?: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  }> {
    const params = new URLSearchParams();
    if (filters?.search) params.set("search", filters.search);
    if (filters?.status) params.set("status", filters.status);
    if (filters?.categoryId) params.set("categoryId", filters.categoryId);
    if (filters?.albumType) params.set("albumType", filters.albumType);
    if (filters?.page) params.set("page", filters.page.toString());
    if (filters?.limit) params.set("limit", filters.limit.toString());

    const query = params.toString();
    return api.get(query ? `${CONTENT_ALBUM}?${query}` : CONTENT_ALBUM);
  },

  async getCreators(): Promise<{ id: string; name: string }[]> {
    return api.get<{ id: string; name: string }[]>(`${CONTENT_ALBUM}/creators`);
  },

  async findOne(id: string): Promise<StudioAlbum> {
    return api.get<StudioAlbum>(`${CONTENT_ALBUM}/${id}`);
  },

  async create(payload: AlbumCreatePayload): Promise<StudioAlbum> {
    return api.post<StudioAlbum>(CONTENT_ALBUM, payload);
  },

  async update(id: string, payload: AlbumUpdatePayload): Promise<StudioAlbum> {
    return api.patch<StudioAlbum>(`${CONTENT_ALBUM}/${id}`, payload);
  },

  async updateStatus(id: string, status: TaxonomyStatus): Promise<StudioAlbum> {
    return api.patch<StudioAlbum>(`${CONTENT_ALBUM}/${id}`, { status });
  },

  async archive(id: string): Promise<StudioAlbum> {
    return api.delete<StudioAlbum>(`${CONTENT_ALBUM}/${id}`);
  },

  async restore(id: string): Promise<StudioAlbum> {
    return api.post<StudioAlbum>(`${CONTENT_ALBUM}/${id}/restore`, {});
  },

  async getVariants(albumId: string): Promise<AlbumLanguageVariant[]> {
    return api.get<AlbumLanguageVariant[]>(
      `${CONTENT_ALBUM}/${albumId}/variants`,
    );
  },

  async createVariant(
    albumId: string,
    payload: AlbumVariantPayload,
  ): Promise<AlbumLanguageVariant> {
    return api.post<AlbumLanguageVariant>(
      `${CONTENT_ALBUM}/${albumId}/variants`,
      payload,
    );
  },

  async updateVariant(
    variantId: string,
    payload: Partial<AlbumVariantPayload>,
  ): Promise<AlbumLanguageVariant> {
    return api.patch<AlbumLanguageVariant>(
      `${CONTENT_ALBUM}/variants/${variantId}`,
      payload,
    );
  },

  async deleteVariant(variantId: string): Promise<void> {
    return api.delete(`${CONTENT_ALBUM}/variants/${variantId}`);
  },

  async getItems(albumId: string): Promise<AlbumItem[]> {
    return api.get<AlbumItem[]>(`${CONTENT_ALBUM}/${albumId}/items`);
  },

  async addItem(
    albumId: string,
    payload: AddAlbumItemPayload,
  ): Promise<AlbumItem> {
    return api.post<AlbumItem>(`${CONTENT_ALBUM}/${albumId}/items`, payload);
  },

  async removeItem(albumId: string, itemId: string): Promise<void> {
    return api.delete(`${CONTENT_ALBUM}/${albumId}/items/${itemId}`);
  },
};
